import { useTranslation } from "react-i18next";
import React, { Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { Link, useLocation } from "react-router-dom";
import { clsx } from "clsx";
import Logo from "./Logo";
import IntegrationSelector from "./IntegrationSelector";
function Sidebar({
    open,
    setOpen,
    navigation,
    company,
    roles,
    company_navigation,
}) {
    const { t } = useTranslation();
    const location = useLocation();
    const SidebarContent = () => (
        <div className="flex grow flex-col gap-y-6 overflow-y-auto bg-white lg:px-6 pb-6">
            <div className="flex h-20 shrink-0 items-center justify-center">
                <Logo className="mx-auto h-10 w-auto" />
            </div>
            {company?.companyType !== "click2secure_validation_company" && (
                <div className="px-1">
                    <IntegrationSelector />
                </div>
            )}
            <nav className="flex flex-1 flex-col">
                <ul className="flex flex-1 flex-col gap-y-8">
                    <li>
                        <ul className="space-y-2">
                            {navigation.map((item) => (
                                <li
                                    key={item.name}
                                    className={clsx(
                                        item?.onlyMerchant && !roles?.includes("company")
                                            ? "hidden"
                                            : "",
                                    )}
                                >
                                    <Link
                                        to={item.href}
                                        className={clsx(
                                            location.pathname === item.href
                                                ? "nav-item-active"
                                                : "text-slate-600 nav-item-hover",
                                            "group flex gap-x-3 p-3 text-sm leading-6 font-bold transition-all duration-500",
                                        )}
                                    >
                                        <item.icon
                                            className={clsx(
                                                location.pathname === item.href
                                                    ? "text-primary-600 scale-110"
                                                    : "text-slate-400 group-hover:text-primary-600 group-hover:scale-110",
                                                "h-6 w-6 shrink-0 transition-all duration-300",
                                            )}
                                            aria-hidden="true"
                                        />
                                        <span className="relative z-10">{item.name}</span>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </li>
                    {roles?.includes("company") && (
                        <li className="">
                            <div className="text-[10px] font-black leading-6 text-slate-400 uppercase tracking-[0.2em] px-4 opacity-70 mb-2">
                                {t("company_navigation.section_title")}
                            </div>
                            <ul className="space-y-2">
                                {company_navigation.map((item) => (
                                    <li key={item.name}>
                                        <Link
                                            to={item.href}
                                            className={clsx(
                                                location.pathname === item.href
                                                    ? "nav-item-active"
                                                    : "text-slate-600 nav-item-hover",
                                                "group flex gap-x-3 p-3 text-sm leading-6 font-bold transition-all duration-500",
                                            )}
                                        >
                                            <span
                                                className={clsx(
                                                    location.pathname === item.href
                                                        ? "text-primary-600 border-primary-200 bg-white shadow-sm ring-1 ring-primary-500/10"
                                                        : "text-slate-500 border-slate-200 group-hover:border-primary-200 group-hover:text-primary-600 group-hover:bg-white group-hover:shadow-sm",
                                                    "flex h-6 w-6 shrink-0 items-center justify-center rounded-lg border text-[10px] font-black transition-all duration-500 group-hover:scale-110",
                                                )}
                                            >
                                                {item.initial}
                                            </span>
                                            <span className="truncate relative z-10">
                                                {item.name}
                                            </span>
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </li>
                    )}
                </ul>
            </nav>
        </div>
    );
    return (
        <>
            <Transition.Root show={open} as={Fragment}>
                <Dialog as="div" className="relative z-50 lg:hidden" onClose={setOpen}>
                    <Transition.Child
                        as={Fragment}
                        enter="transition-opacity ease-linear duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="transition-opacity ease-linear duration-300"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm" />
                    </Transition.Child>

                    <div className="fixed inset-0 flex">
                        <Transition.Child
                            as={Fragment}
                            enter="transition ease-in-out duration-300 transform"
                            enterFrom="ltr:-translate-x-full rtl:translate-x-full"
                            enterTo="translate-x-0"
                            leave="transition ease-in-out duration-300 transform"
                            leaveFrom="translate-x-0"
                            leaveTo="ltr:-translate-x-full rtl:translate-x-full"
                        >
                            <Dialog.Panel className="relative me-16 flex w-full max-w-xs flex-1">
                                <Transition.Child
                                    as={Fragment}
                                    enter="ease-in-out duration-300"
                                    enterFrom="opacity-0"
                                    enterTo="opacity-100"
                                    leave="ease-in-out duration-300"
                                    leaveFrom="opacity-100"
                                    leaveTo="opacity-0"
                                >
                                    <div className="absolute start-full top-0 flex w-16 justify-center pt-5">
                                        <button
                                            type="button"
                                            className="-m-2.5 p-2.5"
                                            onClick={() => setOpen(false)}
                                        >
                                            <span className="sr-only">{t("Close sidebar")}</span>
                                            <XMarkIcon
                                                className="h-6 w-6 text-white"
                                                aria-hidden="true"
                                            />
                                        </button>
                                    </div>
                                </Transition.Child>
                                <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-white px-3 lg:px-6 pb-4">
                                    <SidebarContent />
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </Dialog>
            </Transition.Root>

            <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col border-e border-slate-200/60 bg-white">
                <SidebarContent />
            </div>
        </>
    );
}
export default Sidebar;
